import type { PaymentOption } from '../api/types';
import { CHAINS } from '../chains';
import { config } from '../config';
import { WalletError, type PayContext, type PayResult, type WalletDescriptor } from './types';

export * from './types';
export { deepLinksFor } from './deeplinks';

/**
 * Wallet integrations are loaded lazily and independently. The EVM module pulls
 * in viem (and, on demand, WalletConnect / Coinbase), the Solana module
 * @solana/web3.js, the Tron module nothing heavy. If any of them fails to load
 * or throws while evaluating, only its buttons disappear: the QR / address /
 * countdown / status core must keep rendering no matter what.
 */
type EvmModule = typeof import('./evm');
type SolanaModule = typeof import('./solana');
type TronModule = typeof import('./tron');

const modules: { evm: EvmModule | null; solana: SolanaModule | null; tron: TronModule | null } = {
  evm: null,
  solana: null,
  tron: null,
};

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

async function load<K extends keyof typeof modules>(
  name: K,
  loader: () => Promise<NonNullable<(typeof modules)[K]>>,
  init: (m: NonNullable<(typeof modules)[K]>) => void,
): Promise<void> {
  try {
    const m = await loader();
    init(m);
    modules[name] = m;
  } catch (e) {
    console.warn(`[coinvello] ${name} wallet support unavailable; QR / address payment still works.`, e);
  }
  notify();
}

let discoveryStarted = false;
export function startDiscovery(): void {
  if (discoveryStarted || typeof window === 'undefined') return;
  discoveryStarted = true;
  void load('evm', () => import('./evm'), (m) => {
    m.startEvmDiscovery();
    m.onEvmWalletsChanged(notify);
  });
  void load('solana', () => import('./solana'), (m) => {
    m.startSolanaDiscovery();
    m.onSolanaWalletsChanged(notify);
  });
  void load('tron', () => import('./tron'), () => {});
}

export function onWalletsChanged(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

const evmOnly = (o: PaymentOption) => CHAINS[o.chain].kind === 'evm';

/** All wallets that can pay the given option, available ones first. Only lists wallets whose module loaded. */
export function walletsFor(option: PaymentOption): WalletDescriptor[] {
  const all: WalletDescriptor[] = [];
  if (modules.evm) {
    all.push(
      ...modules.evm.listInjectedWallets(),
      {
        id: 'walletconnect',
        name: 'WalletConnect',
        available: !!config.walletConnectProjectId,
        supports: evmOnly,
      },
      { id: 'coinbase', name: 'Coinbase Wallet', available: true, supports: evmOnly },
    );
  }
  if (modules.solana) all.push(...modules.solana.listSolanaWallets());
  if (modules.tron) all.push(...modules.tron.listTronWallets());
  // Coinbase SDK and the injected Coinbase extension would duplicate.
  const seen = new Set<string>();
  return all
    .filter((w) => w.supports(option))
    // WalletConnect without a projectId is a deployment gap, not something the payer can fix.
    .filter((w) => w.id !== 'walletconnect' || w.available)
    .filter((w) => {
      const key = w.name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => Number(b.available) - Number(a.available));
}

export async function pay(wallet: WalletDescriptor, ctx: PayContext): Promise<PayResult> {
  const id = wallet.id;
  if (id.startsWith('injected:') || id === 'walletconnect' || id === 'coinbase') {
    const evm = modules.evm;
    if (!evm) throw new WalletError('unsupported', 'EVM wallet support failed to load');
    if (id === 'walletconnect') return evm.payWithEip1193(await evm.getWalletConnectProvider(ctx.option), ctx);
    if (id === 'coinbase') return evm.payWithEip1193(await evm.getCoinbaseProvider(ctx.option), ctx);
    const provider = evm.resolveInjected(id);
    if (!provider) throw new WalletError('not_installed', `${wallet.name} is not installed`);
    return evm.payWithEip1193(provider, ctx);
  }
  if (id === 'phantom' || id.startsWith('solana-standard:')) {
    if (!modules.solana) throw new WalletError('unsupported', 'Solana wallet support failed to load');
    return modules.solana.payWithSolana(id, ctx);
  }
  if (id === 'tronlink') {
    if (!modules.tron) throw new WalletError('unsupported', 'Tron wallet support failed to load');
    return modules.tron.payWithTron(ctx);
  }
  throw new WalletError('unsupported', `Unknown wallet ${id}`);
}
